import { isAnyOf } from '@reduxjs/toolkit'
import { brakeRatioCheckId, runBrakeRatioCheck } from '#app/store/middlewares/configurationRequests/checks/runBrakeRatioCheck'
import { dvsCheckId, runDvsCheck } from '#app/store/middlewares/configurationRequests/checks/runDvsCheck'
import { oasExtendedCheckId, oasTcrVcrCheckId, runOasCheck } from '#app/store/middlewares/configurationRequests/checks/runOasCheck'
import { runWeightCheck, weightCheckId } from '#app/store/middlewares/configurationRequests/checks/runWeightCheck'
import { runTyreCheck } from '#store/middlewares/configurationRequests/checks/runTyreCheck'
import {
  _updateConfigurationCheck,
  abortConfigurationChecks,
  type LocalConfigurationId,
  runConfigurationChecks,
  type TruckConfiguration,
} from '#store/slices/configurationSlice.ts'
import type { CheckResultUpdate } from '#types/ConfigurationCheck'
import { isFinished } from '#utils/checkHelpers'
import type { AppListenerApi, AppMiddleware } from '../configurationRequestMiddleware'

const checkIds = [dvsCheckId, weightCheckId, oasTcrVcrCheckId, brakeRatioCheckId, oasExtendedCheckId]

async function doRunConfigurationChecks(api: AppListenerApi, configuration: TruckConfiguration, forceRetry: boolean, controller: AbortController) {
  let lastResult = configuration.checks[configuration.ecmVersion]
  if ((lastResult && forceRetry && isFinished(lastResult.status)) || lastResult?.status === 'aborted') {
    api.dispatch(
      _updateConfigurationCheck({
        configurationId: configuration.id,
        ecmVersion: configuration.ecmVersion,
        clear: true,
      }),
    )
    lastResult = undefined
  }

  if (lastResult && lastResult.status !== 'aborted') {
    console.log('Configuration already checked or checking')
    return
  }

  api.dispatch(
    _updateConfigurationCheck({
      configurationId: configuration.id,
      ecmVersion: configuration.ecmVersion,
    }),
  )

  const abortListener = () => {
    const endTime = Date.now()
    for (const id of checkIds) {
      api.dispatch(
        _updateConfigurationCheck({
          configurationId: configuration.id,
          ecmVersion: configuration.ecmVersion,
          update: {
            id,
            status: 'aborted',
            endTime,
          },
        }),
      )
    }

    api.dispatch(
      _updateConfigurationCheck({
        configurationId: configuration.id,
        ecmVersion: configuration.ecmVersion,
        finished: true,
      }),
    )
  }
  controller.signal.addEventListener('abort', abortListener)

  const updater = (result: CheckResultUpdate) => {
    // signals cannot be updated anymore when it has been aborted
    if (controller.signal.aborted) {
      return
    }
    // console.log('Configuration Check Updated ', result)
    api.dispatch(
      _updateConfigurationCheck({
        configurationId: configuration.id,
        ecmVersion: configuration.ecmVersion,
        update: {
          ...result,
          endTime: result.endTime ?? (isFinished(result.status) ? Date.now() : undefined),
        },
      }),
    )
  }

  await Promise.allSettled([
    runOasCheck(api, configuration, updater),
    runOasCheck(api, configuration, updater, 'extended'),
    runBrakeRatioCheck(api, configuration, updater),
    runDvsCheck(api, configuration, updater),
    runWeightCheck(api, configuration, updater),
    runTyreCheck(api, configuration, updater),
  ])

  controller.signal.removeEventListener('abort', abortListener)

  if (controller.signal.aborted) {
    return
  }

  api.dispatch(
    _updateConfigurationCheck({
      configurationId: configuration.id,
      ecmVersion: configuration.ecmVersion,
      finished: true,
    }),
  )
}

const configurationCheckSignals: Partial<Record<LocalConfigurationId, AbortController>> = {}

export const configurationCheckListener = (middleware: AppMiddleware) => {
  middleware.startListening({
    matcher: isAnyOf(abortConfigurationChecks.match, runConfigurationChecks.match),
    effect: (action, listenerApi) => {
      if (abortConfigurationChecks.match(action)) {
        const {
          payload: { id },
        } = action as ReturnType<typeof abortConfigurationChecks>
        if (!id) {
          return
        }
        configurationCheckSignals[id]?.abort()
        configurationCheckSignals[id] = undefined
        return
      }

      if (runConfigurationChecks.match(action)) {
        const {
          payload: { ecmVersion, id, forceRetry },
        } = action as ReturnType<typeof runConfigurationChecks>
        if (!id) {
          return
        }
        configurationCheckSignals[id]?.abort()

        const controller = new AbortController()
        configurationCheckSignals[id] = controller

        const configuration = listenerApi.getState().truckConfigurations.configurations[id]

        if (!configuration) {
          throw new Error('Configuration not found')
        }

        if (configuration.ecmVersion !== ecmVersion) {
          throw new Error('Configuration ecmVersion mismatch')
        }

        if (configuration.step.requested !== 'DetailedStep') {
          throw new Error(`Expected Configuration step is not 'DetailedStep', was ${configuration.step.requested}`)
        }

        void doRunConfigurationChecks(listenerApi, configuration, !!forceRetry, controller)
        return
      }
    },
  })
}
